// The card on file, on a customer's Money tab (BL-009): what the screen says and decides, kept out of
// the component so it can be tested without a browser. The server words each card ("Visa ending
// 1111, expires 02/28") and every refusal; this only lists, chooses and reads the card page's answer.

/** cards.list / cards.finishSave: one card on the customer's file. */
export interface Card {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  label: string;
  expired: boolean;
  addedAt: string;
  renews: { membershipId: string; name: string; endsOn: string }[];
}

/** Who keeps cards on a customer's file (customers.edit): never the bookkeeper or a technician. */
export const CARD_PERMS = ['customers.edit'];
export const keepsCards = (held: readonly string[]) => CARD_PERMS.some((p) => held.includes(p));

/**
 * What the office does with the cards here (BL-088). A card on file is Flowology's own, but while the
 * company mirrors ServiceTitan no card is saved (the card processor refuses it, st-mirror §3.5), and a
 * card a membership renews to stays: the renewal is ServiceTitan's membership's. Any other card comes
 * off the file as ever.
 */
export const cardRights = (mirroring: boolean) => ({
  save: !mirroring,
  remove: (c: Pick<Card, 'renews'>) => !(mirroring && c.renews.length > 0),
});

/** The processor that serves its own stand-in page here (the Helcim fake, the sandbox's). */
export const FAKE_PROCESSOR = 'fake_helcim';
/** Where Helcim's own card page answers from. */
export const HELCIM_ORIGIN = 'https://secure.helcim.app';

/** Helcim's page script (HelcimPay.js): the one script the office pages' CSP allows from Helcim. */
export const HELCIM_SCRIPT = `${HELCIM_ORIGIN}/helcim-pay/services/start.js`;

/** "Renews Comfort Club, ends Nov 5, 2027" for each membership whose renewal goes to the card. */
export function renewsText(c: Pick<Card, 'renews'>, day: (iso: string) => string): string | null {
  if (!c.renews.length) return null;
  return `Renews ${c.renews.map((r) => `${r.name} (ends ${day(r.endsOn)})`).join(', ')}`;
}

/** The cards a removed card's renewals may move to: the customer's others that haven't expired. */
export const replacements = (cards: readonly Card[], removing: Card) =>
  cards.filter((c) => c.id !== removing.id && !c.expired);

/** What removing a card will do, in words, before the person decides. */
export function removeOutcome(card: Card, replacement: Card | undefined): string {
  if (!card.renews.length) return `${card.label} comes off their file and out of the card processor’s vault.`;
  const what = card.renews.map((r) => r.name).join(', ');
  return replacement
    ? `${card.label} comes off their file. ${what} will renew to ${replacement.label} instead.`
    : `${card.label} is the card for ${what}’s renewal. Choose another card for it first.`;
}

/**
 * The card page's answer, from a window message: the processor's data and hash to keep the card,
 * its own words when it didn't save one, or null for a message that isn't this page's (another
 * frame, another origin, another page's token).
 */
export function cardPageAnswer(
  ev: { origin: string; data: unknown },
  page: { checkoutToken: string; processor: string },
  ownOrigin: string,
): { data: Record<string, string>; hash: string } | { error: string } | null {
  const d = ev.data as { eventName?: unknown; eventStatus?: unknown; eventMessage?: unknown } | null;
  if (!d || d.eventName !== `helcim-pay-js-${page.checkoutToken}`) return null;
  if (ev.origin !== (page.processor === FAKE_PROCESSOR ? ownOrigin : HELCIM_ORIGIN)) return null;
  let msg: { data?: Record<string, unknown>; hash?: unknown; message?: unknown } = {};
  try {
    msg =
      typeof d.eventMessage === 'string'
        ? JSON.parse(d.eventMessage)
        : ((d.eventMessage ?? {}) as typeof msg);
  } catch {
    msg = {};
  }
  if (d.eventStatus === 'SUCCESS' && msg.data && typeof msg.hash === 'string')
    return {
      data: Object.fromEntries(Object.entries(msg.data).map(([k, v]) => [k, String(v ?? '')])),
      hash: msg.hash,
    };
  return {
    error:
      typeof msg.message === 'string' && msg.message
        ? `The card wasn’t saved: ${msg.message.slice(0, 200)}`
        : 'The card wasn’t saved. Try again, or another card.',
  };
}
